import { useEffect, useMemo, useState } from "react";
import { Plus, X, Filter as FilterIcon, Layers } from "lucide-react";
import { Button, DateTimePicker, Input, Select } from "@/components/ui";
import { OPERATORS_BY_KIND, STANDARD_FIELDS, type FieldOption, type FieldKind } from "./filter-types";
import { api } from "@/lib/api";
import type { CustomField, Tag, User } from "@/lib/types";
import { cn } from "@/lib/utils";

export interface FilterRule { field: string; op: string; value?: unknown }
export interface FilterSpec {
  match?: "all" | "any";
  rules?: FilterRule[];
  /** Nested groups joined by the parent's `match`. Lets us express
   *  AND/OR combinations at arbitrary depth. */
  groups?: FilterSpec[];
}

interface BuilderContext {
  fields: FieldOption[];
  grouped: [string, FieldOption[]][];
  tags: Tag[];
  users: User[];
}

export function SmartFilterBuilder({
  filter,
  onChange,
  className,
}: {
  filter: FilterSpec;
  onChange: (next: FilterSpec) => void;
  className?: string;
}) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  useEffect(() => {
    api.get<{ tags: Tag[] }>("/tags").then((d) => setTags(d.tags)).catch(() => {});
    api.get<{ users: User[] }>("/settings/users").then((d) => setUsers(d.users)).catch(() => {});
    api.get<{ fields: CustomField[] }>("/custom-fields").then((d) => setCustomFields(d.fields)).catch(() => {});
  }, []);

  const fields = useMemo<FieldOption[]>(() => {
    const customMapped: FieldOption[] = customFields.map((f) => ({
      field: `custom.${f.key}`,
      label: f.label,
      kind: customKindToFilterKind(f.field_type),
      options: f.options,
      group: f.group_name || "Custom fields",
    }));
    return [...STANDARD_FIELDS, ...customMapped];
  }, [customFields]);

  const grouped = useMemo(() => {
    const map = new Map<string, FieldOption[]>();
    for (const f of fields) {
      const g = f.group ?? "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(f);
    }
    return Array.from(map.entries());
  }, [fields]);

  const ctx: BuilderContext = { fields, grouped, tags, users };

  return (
    <div className={cn("space-y-2", className)}>
      <GroupBuilder spec={filter} onChange={onChange} ctx={ctx} depth={0} root />
    </div>
  );
}

function GroupBuilder({
  spec, onChange, ctx, depth, root,
}: {
  spec: FilterSpec;
  onChange: (next: FilterSpec) => void;
  ctx: BuilderContext;
  depth: number;
  root?: boolean;
}) {
  const match = spec.match ?? "all";
  const rules = spec.rules ?? [];
  const groups = spec.groups ?? [];
  const isEmpty = rules.length === 0 && groups.length === 0;

  const update = (patch: Partial<FilterSpec>) => onChange({ match, rules, groups, ...patch });
  const updateRule = (idx: number, p: Partial<FilterRule>) => {
    const next = rules.slice();
    next[idx] = { ...next[idx]!, ...p };
    update({ rules: next });
  };
  const removeRule = (idx: number) => {
    const next = rules.slice();
    next.splice(idx, 1);
    update({ rules: next });
  };
  const addRule = () => update({ rules: [...rules, { field: "first_name", op: "contains", value: "" }] });
  const updateGroup = (idx: number, g: FilterSpec) => {
    const next = groups.slice();
    next[idx] = g;
    update({ groups: next });
  };
  const removeGroup = (idx: number) => {
    const next = groups.slice();
    next.splice(idx, 1);
    update({ groups: next });
  };
  const addGroup = () => update({
    groups: [
      ...groups,
      { match: match === "all" ? "any" : "all", rules: [{ field: "first_name", op: "contains", value: "" }] },
    ],
  });

  return (
    <div
      className={cn(
        "rounded-lg",
        !root && "border border-zinc-800 bg-zinc-900/40 p-3",
      )}
    >
      {/* Match toggle — ALL / ANY */}
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        {root ? (
          <FilterIcon className="h-3.5 w-3.5 text-zinc-500" />
        ) : (
          <Layers className="h-3.5 w-3.5 text-zinc-500" />
        )}
        <span className="text-zinc-500">{root ? "Match" : "Sub-group:"}</span>
        <div className="inline-flex rounded-lg border border-zinc-800 p-0.5">
          {(["all", "any"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => update({ match: m })}
              className={cn(
                "px-2.5 py-0.5 text-xs font-medium rounded-md transition-colors",
                match === m ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
              )}
            >
              {m === "all" ? "ALL" : "ANY"}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-500">
          {match === "all" ? "of these are true" : "of these is true"}
        </span>
        {!root && (
          <button
            type="button"
            onClick={() => onChange({ match: "all", rules: [], groups: [] })}
            className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
            aria-label="Remove group"
            title="Remove group"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isEmpty && root && (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 px-3 py-3 text-center">
          <p className="text-xs text-zinc-500">No conditions — list shows all contacts.</p>
        </div>
      )}

      <ul className="space-y-1.5">
        {rules.map((rule, idx) => (
          <li key={`r-${idx}`}>
            <RuleRow
              rule={rule}
              ctx={ctx}
              onChange={(p) => updateRule(idx, p)}
              onRemove={() => removeRule(idx)}
            />
          </li>
        ))}
        {groups.map((g, idx) => (
          <li key={`g-${idx}`}>
            <GroupBuilder
              spec={g}
              onChange={(next) => {
                if ((next.rules?.length ?? 0) === 0 && (next.groups?.length ?? 0) === 0) {
                  removeGroup(idx);
                } else {
                  updateGroup(idx, next);
                }
              }}
              ctx={ctx}
              depth={depth + 1}
            />
          </li>
        ))}
      </ul>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <Button variant="outline" size="sm" leftIcon={<Plus className="h-3 w-3" />} onClick={addRule}>
          Add condition
        </Button>
        {depth < 3 && (
          <Button variant="ghost" size="sm" leftIcon={<Layers className="h-3 w-3" />} onClick={addGroup}>
            Add sub-group
          </Button>
        )}
      </div>
    </div>
  );
}

function RuleRow({
  rule, ctx, onChange, onRemove,
}: {
  rule: FilterRule;
  ctx: BuilderContext;
  onChange: (patch: Partial<FilterRule>) => void;
  onRemove: () => void;
}) {
  const fieldDef = ctx.fields.find((f) => f.field === rule.field);
  const operators = fieldDef ? OPERATORS_BY_KIND[fieldDef.kind] : [];
  const opDef = operators.find((o) => o.op === rule.op);

  const onFieldChange = (newField: string) => {
    const newDef = ctx.fields.find((f) => f.field === newField);
    if (!newDef) return;
    const newOps = OPERATORS_BY_KIND[newDef.kind];
    const newOp = newOps[0]?.op ?? "eq";
    onChange({ field: newField, op: newOp, value: undefined });
  };

  return (
    <div className="grid grid-cols-1 gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-center">
      <Select value={rule.field} onChange={(e) => onFieldChange(e.target.value)} className="h-9 text-sm">
        {ctx.grouped.map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
          </optgroup>
        ))}
      </Select>

      <Select value={rule.op} onChange={(e) => onChange({ op: e.target.value, value: undefined })} className="h-9 text-sm sm:w-44">
        {operators.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
      </Select>

      <ValueInput
        rule={rule}
        opDef={opDef}
        fieldDef={fieldDef}
        ctx={ctx}
        onChange={(v) => onChange({ value: v })}
      />

      <button
        type="button"
        onClick={onRemove}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
        aria-label="Remove condition"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ValueInput({
  rule, opDef, fieldDef, ctx, onChange,
}: {
  rule: FilterRule;
  opDef: { valueKind: string } | undefined;
  fieldDef: FieldOption | undefined;
  ctx: BuilderContext;
  onChange: (v: unknown) => void;
}) {
  if (!opDef) return <div />;
  const v = rule.value;
  switch (opDef.valueKind) {
    case "none":
      return <div className="text-xs text-zinc-600 italic px-1">—</div>;
    case "boolean":
      return <div className="text-xs text-zinc-600 italic px-1">—</div>;
    case "text":
      return <Input value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value)} className="h-9" />;
    case "number":
      return <Input type="number" value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="h-9" />;
    case "date":
      return (
        <DateTimePicker
          value={v == null ? "" : String(v)}
          onChange={(next) => onChange(next || null)}
          includeTime={false}
          placeholder="Pick a date"
        />
      );
    case "tag":
      return (
        <Select value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm">
          <option value="">Select tag…</option>
          {ctx.tags.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </Select>
      );
    case "options": {
      if (fieldDef?.kind === "owner") {
        return (
          <Select value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm">
            <option value="">Select user…</option>
            {ctx.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        );
      }
      const opts = fieldDef?.options ?? [];
      return (
        <Select value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value)} className="h-9 text-sm">
          <option value="">Select…</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      );
    }
    default:
      return <Input value={v == null ? "" : String(v)} onChange={(e) => onChange(e.target.value)} className="h-9" />;
  }
}

function customKindToFilterKind(t: CustomField["field_type"]): FieldKind {
  switch (t) {
    case "longtext": case "url": case "email": case "phone": return "text";
    case "number":      return "number";
    case "boolean":     return "boolean";
    case "date":        return "date";
    case "datetime":    return "datetime";
    case "dropdown":    return "dropdown";
    case "multiselect": return "multiselect";
    default:            return "text";
  }
}
